// Every workflow run of the workspace, newest first: rate-file runs (from "Try it" and from
// imports) and Elariom's conversations (Teams and the Ask page). GET /api/admin/elariom/:org/executions.

// React imports
import { useEffect, useState, useCallback } from 'react';

// Other imports
import axios from 'axios';
import dayjs from 'dayjs';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import { useTheme } from '@mui/material/styles';

// Local imports
import { SectionTitle } from './ElariomParts.js';

const STATUS = {
    success: { label: 'Success', tone: 'success' },
    error: { label: 'Error', tone: 'error' },
    running: { label: 'Running', tone: 'warning' },
    stopped: { label: 'Stopped', tone: 'muted' },
};

function formatRunTime(ms) {
    if (ms === null || ms === undefined) return '—';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60 * 1000) return `${(ms / 1000).toFixed(3).replace(/\.?0+$/, '')}s`;
    const s = Math.round(ms / 1000);
    return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

function Status({ status, error }) {
    const theme = useTheme();
    const s = STATUS[status] || { label: status || '—', tone: 'muted' };
    const color = s.tone === 'muted' ? theme.palette.text.secondary : theme.palette[s.tone].main;
    const dot = <Box sx={{ width: '7px', height: '7px', borderRadius: '50%', background: color }} />;
    return (
        <Tooltip title={error || ''}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color, fontSize: '12px' }}>{dot}{s.label}</Box>
        </Tooltip>
    );
}

function ElariomExecutions({ org, accesstoken }) {
    const theme = useTheme();
    const [ executions, setExecutions ] = useState(null);
    const [ error, setError ] = useState(null);
    const [ loading, setLoading ] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(`/api/admin/elariom/${org.id}/executions?accesstoken=${accesstoken}`);
            setExecutions(data);
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        }
        setLoading(false);
    }, [org?.id, accesstoken]);

    useEffect(() => { if (org?.id) load(); }, [org?.id, load]);
    // While something is running, check again every 10 seconds.
    const running = (executions || []).filter(e => e.status === 'running');
    useEffect(() => {
        if (!running.length) return undefined;
        const t = setTimeout(load, 10000);
        return () => clearTimeout(t);
    }, [running.length, executions, load]);

    const cell = { fontSize: '12px', padding: '8px 10px' };
    const head = { ...cell, color: theme.palette.text.secondary, fontWeight: 500 };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <SectionTitle
                title='Executions'
                blurb='Every run of every workflow on this workspace: rate files parsed, and questions Elariom answered in Teams and here.'
                right={<Button variant='outlined' size='small' onClick={load} disabled={loading}>Refresh</Button>}
            />
            {loading && <LinearProgress />}
            {error && <Typography sx={{ fontSize: '13px', color: theme.palette.error.main }}>Couldn't load the executions: {error}</Typography>}
            {executions && (
                <>
                    <Typography sx={{ fontSize: '13px', color: running.length ? theme.palette.text.primary : theme.palette.text.secondary }}>
                        {running.length ? `${running.length} running now` : 'No active executions'}
                    </Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size='small'>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={head}>Workflow</TableCell>
                                    <TableCell sx={head}>Status</TableCell>
                                    <TableCell sx={head}>Started</TableCell>
                                    <TableCell sx={head}>Run time</TableCell>
                                    <TableCell sx={head}>Exec. ID</TableCell>
                                    <TableCell sx={head}>Details</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {executions.map(e => (
                                    <TableRow key={`${e.channel}-${e.id}`} hover>
                                        <TableCell sx={cell}>
                                            {e.workflow}
                                            <Typography component='span' sx={{ fontSize: '11px', color: theme.palette.text.secondary, marginLeft: '6px' }}>{e.channel}</Typography>
                                        </TableCell>
                                        <TableCell sx={cell}><Status status={e.status} error={e.error} /></TableCell>
                                        <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>{dayjs(e.at).format('MMM D, HH:mm:ss')}</TableCell>
                                        <TableCell sx={{ ...cell, fontVariantNumeric: 'tabular-nums' }}>{e.status === 'running' ? '…' : formatRunTime(e.durationMs)}</TableCell>
                                        <TableCell sx={{ ...cell, fontVariantNumeric: 'tabular-nums' }}>{e.number}</TableCell>
                                        <TableCell sx={{ ...cell, color: theme.palette.text.secondary, maxWidth: '360px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.detail || ''}>
                                            {e.detail || ''}{e.by ? ` · ${e.by}` : ''}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                    <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
                        {executions.length ? 'No more executions to fetch' : 'No executions yet'}
                    </Typography>
                </>
            )}
        </Box>
    );
}

export default ElariomExecutions;
